import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft, Truck } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { apiGet, apiPost } from '../api';
import { API_BASE_URL } from '../api';

const BACKEND_URL = API_BASE_URL;
const API = `${BACKEND_URL}/api`;

const CartPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { fetchCartCount } = useCart();
  const { isRTL, currency, formatMoney } = useLanguage();
  const [cart, setCart] = useState(null);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [countryCode, setCountryCode] = useState('SA');
  const [shippingEstimate, setShippingEstimate] = useState({ 
    loading: false, 
    cost: 0, 
    days: null, 
    error: null 
  });

  useEffect(() => {
    if (user) {
      fetchCart();
      detectCountry();
    } else {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (cart && cart.items.length > 0 && countryCode) {
      estimateShipping();
    }
  }, [cart, countryCode, currency]);

  const detectCountry = async () => {
    try {
      const data = await apiGet('/api/geo/detect');
      if (data && data.country_code) {
        setCountryCode(data.country_code);
      }
    } catch (error) {
      console.error('Error detecting country:', error);
      // Default to Saudi Arabia if detection fails
      setCountryCode('SA');
    }
  };

  const estimateShipping = async () => {
    try {
      setShippingEstimate({ loading: true, cost: 0, days: null, error: null });
      const payload = {
        country_code: countryCode,
        preferred: 'fastest',
        currency: currency || 'SAR',
        markup_pct: 10,
        items: (cart?.items || []).map((it) => ({ 
          product_id: it.product_id, 
          quantity: it.quantity 
        }))
      };
      
      const data = await apiPost('/api/shipping/estimate', payload);
      
      const cost = data?.shipping_cost?.[currency] ?? 0;
      const days = data?.estimated_days || null;
      setShippingEstimate({ loading: false, cost, days, error: null });
    } catch (e) {
      console.error('estimateShipping error', e);
      // Check if error is 400 (unavailable)
      if (e.message && e.message.includes('400')) {
        setShippingEstimate({ loading: false, cost: 0, days: null, error: 'unavailable' });
        toast.warning(isRTL ? 'الشحن غير متاح لبلدك' : 'Shipping unavailable for your country');
      } else {
        setShippingEstimate({ loading: false, cost: 15, days: { min: 3, max: 7 }, error: 'fallback' });
      }
    }
  };

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`);
      setCart(response.data);
      
      // Fetch product details for cart items
      const productPromises = response.data.items.map(item => 
        axios.get(`${API}/products/${item.product_id}`)
      );
      const productResponses = await Promise.all(productPromises);
      
      const productsMap = {};
      productResponses.forEach(res => {
        productsMap[res.data.id] = res.data;
      });
      setProducts(productsMap);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast.error(isRTL ? 'فشل في تحميل السلة' : 'Failed to load cart');
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    
    try {
      // Remove item first, then add with new quantity
      await axios.delete(`${API}/cart/remove/${productId}`);
      if (newQuantity > 0) {
        await axios.post(`${API}/cart/add?product_id=${productId}&quantity=${newQuantity}`);
      }
      await fetchCart(); // Refresh cart
      await fetchCartCount(); // Update cart count in navbar
      toast.success(isRTL ? 'تم تحديث السلة' : 'Cart updated');
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast.error(isRTL ? 'فشل في تحديث الكمية' : 'Failed to update quantity');
    }
  };

  const removeItem = async (productId) => {
    try {
      await axios.delete(`${API}/cart/remove/${productId}`);
      await fetchCart(); // Refresh cart
      await fetchCartCount(); // Update cart count in navbar
      toast.success(isRTL ? 'تم إزالة المنتج من السلة' : 'Item removed from cart');
    } catch (error) {
      console.error('Error removing item:', error);
      toast.error(isRTL ? 'فشل في إزالة المنتج' : 'Failed to remove item');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
        <Card className="luxury-card p-8 text-center max-w-md">
          <ShoppingBag className="h-16 w-16 mx-auto mb-4 text-amber-600" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{isRTL ? 'يجب تسجيل الدخول' : 'Sign in required'}</h2>
          <p className="text-gray-600 mb-6">
            {isRTL ? 'يجب تسجيل الدخول لعرض سلة التسوق' : 'Sign in to see your shopping cart'}
          </p>
          <Link to="/auth">
            <Button className="btn-luxury">
              {isRTL ? 'تسجيل الدخول' : 'Sign in'}
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <ShoppingBag className="h-24 w-24 mx-auto mb-6 text-gray-400" />
            <h1 className="font-display text-4xl font-bold text-gray-900 mb-4" data-testid="empty-cart-title">
              {isRTL ? 'سلة التسوق فارغة' : 'Your cart is empty'}
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {isRTL ? 'لم تقم بإضافة أي منتجات بعد' : 'You have not added any products yet'}
            </p>
            <div className="space-y-4">
              <Link to="/products">
                <Button className="btn-luxury" data-testid="continue-shopping-button">
                  {isRTL ? 'تابع التسوق' : 'Continue shopping'}
                  <ArrowLeft className="ms-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-display text-4xl font-bold text-gray-900 mb-4" data-testid="cart-page-title">
            {isRTL ? 'سلة التسوق' : 'Shopping cart'}
          </h1>
          <p className="text-xl text-gray-600">
            {isRTL ? 'مراجعة وتعديل منتجاتك قبل إتمام الطلب' : 'Review and adjust your items before checkout'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item) => {
              const product = products[item.product_id];
              if (!product) return null;
              
              return (
                <Card key={item.product_id} className="luxury-card p-6" data-testid={`cart-item-${item.product_id}`}>
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-shrink-0">
                      <Link to={`/product/${product.id}`}>
                        <img 
                          src={product.images[0]} 
                          alt={isRTL ? (product.name_ar || product.name) : (product.name_en || product.name)}
                          className="w-full md:w-32 h-32 object-cover rounded-lg hover:scale-105 transition-transform duration-300"
                        />
                      </Link>
                    </div>
                    
                    <div className="flex-grow">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <Link to={`/product/${product.id}`}>
                            <h3 className="font-bold text-lg text-gray-900 hover:text-amber-600 transition-colors">
                              {isRTL ? (product.name_ar || product.name) : (product.name_en || product.name)}
                            </h3>
                          </Link>
                          <p className="text-gray-600 text-sm mt-1">
                            {product.category.replace('_', ' ')}
                          </p>
                        </div>
                        <button 
                          onClick={() => removeItem(item.product_id)}
                          className="text-red-500 hover:text-red-700 p-2"
                          data-testid={`remove-item-${item.product_id}`}
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center border border-gray-300 rounded-lg">
                            <button 
                              onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                              className="p-2 hover:bg-gray-100 transition-colors"
                              disabled={item.quantity <= 1}
                              data-testid={`decrease-quantity-${item.product_id}`}
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="px-4 py-2 min-w-[3rem] text-center" data-testid={`item-quantity-${item.product_id}`}>
                              {item.quantity}
                            </span>
                            <button 
                              onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                              className="p-2 hover:bg-gray-100 transition-colors"
                              data-testid={`increase-quantity-${item.product_id}`}
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className="text-2xl font-bold text-amber-600" data-testid={`item-total-${item.product_id}`}>
                            {formatMoney(item.price * item.quantity)}
                          </div>
                          <div className="text-sm text-gray-600">
                            {formatMoney(item.price)} {isRTL ? 'للقطعة' : 'each'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="luxury-card p-6 sticky top-24">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                {isRTL ? 'ملخص الطلب' : 'Order Summary'}
              </h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {isRTL ? 'المجموع الجزئي:' : 'Subtotal:'}
                  </span>
                  <span className="font-medium" data-testid="subtotal">
                    {formatMoney(cart.total_amount)}
                  </span>
                </div>
                
                <div className="flex justify-between items-start">
                  <span className="text-gray-600 flex items-center gap-2">
                    <Truck className="h-4 w-4" />
                    {isRTL ? 'الشحن:' : 'Shipping:'}
                  </span>
                  <div className="text-right">
                    {shippingEstimate.loading ? (
                      <span className="text-sm text-gray-500">
                        {isRTL ? 'جاري الحساب...' : 'Calculating...'}
                      </span>
                    ) : shippingEstimate.error === 'unavailable' ? (
                      <span className="text-sm text-red-600">
                        {isRTL ? 'غير متاح' : 'Unavailable'}
                      </span>
                    ) : (
                      <>
                        <span className="font-medium">
                          {formatMoney(shippingEstimate.cost)}
                        </span>
                        {shippingEstimate.days && (
                          <div className="text-xs text-gray-500 mt-1">
                            {shippingEstimate.days} {isRTL ? 'أيام' : 'days'}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                
                <hr className="border-gray-200" />
                <div className="flex justify-between text-xl font-bold">
                  <span>{isRTL ? 'المجموع:' : 'Total:'}</span>
                  <span className="text-amber-600" data-testid="total-amount">
                    {formatMoney(cart.total_amount + (shippingEstimate.cost || 0))}
                  </span>
                </div>
              </div>
              
              <div className="space-y-3">
                <Link to="/checkout" className="block">
                  <Button 
                    className="btn-luxury w-full" 
                    data-testid="checkout-button"
                    disabled={shippingEstimate.error === 'unavailable'}
                  >
                    {isRTL ? 'إتمام الطلب' : 'Proceed to Checkout'}
                    <ArrowLeft className="ms-2 h-5 w-5" />
                  </Button>
                </Link>
                {shippingEstimate.error === 'unavailable' && (
                  <p className="text-sm text-red-600 text-center">
                    {isRTL ? 'الشحن غير متاح لبلدك حالياً' : 'Shipping unavailable for your country'}
                  </p>
                )}
                <Link to="/products" className="block">
                  <Button variant="outline" className="w-full">
                    {isRTL ? 'متابعة التسوق' : 'Continue Shopping'}
                  </Button>
                </Link>
              </div>
              
              {/* Security badges */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="text-center text-sm text-gray-600">
                  <p className="mb-2">🔒 {isRTL ? 'دفع آمن ومشفر' : 'Secure, encrypted payment'}</p>
                  <p>🚚 {isRTL ? 'شحن سريع وآمن' : 'Fast, safe shipping'}</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;